import { useEffect, useState } from 'react'
import { useSession } from './lib/auth-client.ts'
import {
  lireSessionLocale,
  ecrireSessionLocale,
  oublierSessionLocale,
} from './lib/client/session-locale.ts'
import { magasin } from './lib/client/store.ts'
import { useMagasin } from './lib/client/hooks.ts'
import { Connexion } from './screens/Connexion.tsx'
import { Inventaire } from './screens/Inventaire.tsx'
import { Courses } from './screens/Courses.tsx'
import { ModeMagasin } from './screens/ModeMagasin.tsx'
import { AjoutRapide } from './screens/AjoutRapide.tsx'
import { Recettes } from './screens/Recettes.tsx'
import { Semaine } from './screens/Semaine.tsx'
import { Reglages } from './screens/Reglages.tsx'
import { Autoriser } from './screens/Autoriser.tsx'
import { BarreOnglets, type Onglet } from './components/BarreOnglets.tsx'
import { PastilleSynchro } from './components/PastilleSynchro.tsx'
import { Toasts } from './components/Toast.tsx'
import { Reglage } from './components/Icone.tsx'
import { enregistrerSw, type EtatMaj } from './lib/client/sw.ts'

/**
 * Routage minimal, sans bibliothèque.
 *
 * Quatre destinations et deux surfaces modales : un routeur complet
 * apporterait du code et des concepts pour un arbre qui tient dans un
 * `switch`. On synchronise malgré tout l'URL, parce que les raccourcis
 * d'écran d'accueil du manifeste pointent vers /courses et /ajout — lancer
 * le mode magasin depuis l'écran verrouillé en arrivant au magasin est le
 * plus gros levier PWA de l'app.
 */
const DEPUIS_URL: Record<string, Onglet> = {
  '/': 'inventaire',
  '/inventaire': 'inventaire',
  '/courses': 'courses',
  '/recettes': 'recettes',
  '/semaine': 'semaine',
}

export function App() {
  /*
   * L'écran de consentement est traité AVANT tout le reste, et hors du
   * routage par onglets : c'est une surface de confiance qui ne doit ni
   * hériter de la coquille de l'app, ni pouvoir être atteinte par un onglet.
   */
  if (location.pathname === '/autoriser') return <Autoriser />
  return <AppConnectee />
}

function AppConnectee() {
  const { data: session, isPending } = useSession()
  const [onglet, setOnglet] = useState<Onglet>(() => DEPUIS_URL[location.pathname] ?? 'inventaire')
  const [ajout, setAjout] = useState(() => location.pathname === '/ajout')
  const [ajoutSaisie, setAjoutSaisie] = useState('')
  const [magasinOuvert, setMagasinOuvert] = useState(false)
  const [reglages, setReglages] = useState(false)
  const [maj, setMaj] = useState<EtatMaj | null>(null)

  useEffect(() => enregistrerSw(setMaj), [])

  /*
   * Session locale lue de façon SYNCHRONE, dès le premier rendu.
   *
   * C'est ce qui évite deux choses : l'éclair d'écran de connexion au
   * démarrage, et surtout la déconnexion hors-ligne — `useSession`
   * interroge le serveur, et sans réseau il ne répond rien.
   */
  const [locale] = useState(lireSessionLocale)
  const distante = session?.user as
    | { id: string; householdId?: string; name?: string }
    | undefined

  useEffect(() => {
    if (!distante) return
    ecrireSessionLocale({
      userId: distante.id,
      householdId: distante.householdId ?? 'foyer',
      prenom: distante.name ?? '',
    })
  }, [distante])

  /*
   * Le serveur a répondu « pas de session » : là, c'est une vraie
   * déconnexion (expiration, révocation), pas un problème de réseau. On
   * oublie la mémoire locale, sinon on resterait bloqué sur une session
   * morte.
   */
  useEffect(() => {
    if (!isPending && session === null && navigator.onLine) oublierSessionLocale()
  }, [isPending, session])

  const utilisateur =
    distante ??
    (locale ? { id: locale.userId, householdId: locale.householdId, name: locale.prenom } : undefined)

  useEffect(() => {
    if (!utilisateur) return
    void magasin.demarrer(utilisateur.id, utilisateur.householdId ?? 'foyer', utilisateur.name)
  }, [utilisateur?.id, utilisateur?.householdId, utilisateur?.name])

  useEffect(() => {
    const chemin = onglet === 'inventaire' ? '/' : `/${onglet}`
    if (location.pathname !== chemin) history.replaceState(null, '', chemin)
  }, [onglet])

  /* Pas de squelette : la session vient d'un cookie, c'est quasi instantané,
     et un squelette mentirait sur une latence qui n'existe pas. */
  if (isPending && !locale) return <div className="min-h-dvh bg-canvas" />
  if (!utilisateur) return <Connexion />

  return (
    <div className="min-h-dvh bg-canvas text-ink">
      <EnTeteFlottant onReglages={() => setReglages(true)} />

      {/*
        `key={onglet}` force React à remonter l'écran à chaque changement.
        L'entrée passe par @starting-style et une TRANSITION, pas une
        keyframe : on change d'onglet une vingtaine de fois par jour, et une
        keyframe interrompue par un second changement repart de zéro.
      */}
      <div key={onglet} className="entree-ecran">
        {onglet === 'inventaire' && <Inventaire
            onOuvrirAjout={(saisie = '') => {
              setAjoutSaisie(saisie)
              setAjout(true)
            }}
          />}
        {onglet === 'courses' && <Courses onModeMagasin={() => setMagasinOuvert(true)} />}
        {onglet === 'recettes' && <Recettes />}
        {onglet === 'semaine' && <Semaine />}
      </div>

      <BarreOnglets
        actif={onglet}
        onChange={setOnglet}
        onAjouter={() => setAjout(true)}
      />

      <AjoutRapide
        ouverte={ajout}
        saisieInitiale={ajoutSaisie}
        onFermer={() => {
          setAjout(false)
          setAjoutSaisie('')
        }}
      />
      {magasinOuvert && <ModeMagasin onSortir={() => setMagasinOuvert(false)} />}
      <Reglages ouverte={reglages} onFermer={() => setReglages(false)} />

      <Toasts />

      {maj?.disponible && <BandeauMaj onAppliquer={maj.appliquer} onIgnorer={() => setMaj(null)} />}
    </div>
  )
}

/**
 * Proposition de mise à jour — jamais imposée.
 *
 * Placée au-dessus de la barre d'onglets et non en plein écran : c'est une
 * information, pas une interruption. Et elle porte un « Plus tard » qui
 * marche vraiment.
 */
function BandeauMaj({ onAppliquer, onIgnorer }: { onAppliquer: () => void; onIgnorer: () => void }) {
  return (
    <div
      className="fixed inset-x-3 z-40 flex items-center gap-3 rounded-sm bg-ink px-4 py-3 text-canvas shadow-pop"
      style={{
        bottom: 'calc(var(--size-bar) + env(safe-area-inset-bottom, 0px) + 12px)',
        animation: 'monter 400ms ease',
      }}
    >
      <span className="flex-1 text-meta">Nouvelle version de Popote disponible.</span>
      <button type="button" onClick={onIgnorer} className="pressable text-meta opacity-70">
        Plus tard
      </button>
      <button type="button" onClick={onAppliquer} className="pressable text-meta font-semibold">
        Recharger
      </button>
    </div>
  )
}

/** La pastille de synchro et l'accès aux réglages, superposés à l'en-tête de
 *  chaque écran. Réglages vit ici et non dans un onglet : il est visité une
 *  fois par mois, il ne mérite pas 20 % de la barre. */
function EnTeteFlottant({ onReglages }: { onReglages: () => void }) {
  const { pret } = useMagasin()
  return (
    <div
      className="pointer-events-none fixed right-3 z-30 flex items-center gap-2"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 10px)' }}
    >
      <div className="pointer-events-auto">
        <PastilleSynchro />
      </div>
      <button
        type="button"
        aria-label="Réglages"
        onClick={onReglages}
        disabled={!pret}
        className="pressable pointer-events-auto flex size-8 items-center justify-center rounded-full bg-surface-sunk text-meta text-muted"
      >
        <Reglage className="size-4" />
      </button>
    </div>
  )
}
